import React from 'react';
import { Edit as EditIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Chit, ChitContent, ChitMain, ChitAvatar, ChitText, ChitStatus } from 'src/components/ui/chit';
import { Popover, PopoverTrigger, PopoverContent } from 'src/components/ui/Popover';
import { getRoleLabel, getRoleIcon, formatDate } from 'src/utils/organizationUtils';

interface MemberCardProps {
  userId: string;
  displayName: string;
  orgEmail: string;
  role: string;
  customRoleName?: string;
  joinedDate: string;
  onAction: (userId: string, action: 'edit' | 'remove') => void;
}

export const MemberCard: React.FC<MemberCardProps> = ({
  userId,
  displayName,
  orgEmail,
  role,
  customRoleName,
  joinedDate,
  onAction
}) => {

  const subtitle = (
    <span className="block">{orgEmail}</span>
  );

  const RoleIcon = getRoleIcon(role);
  const description = (
    <span className="flex items-center gap-2">
      <ChitStatus variant="default" icon={RoleIcon}>
        {getRoleLabel(role, customRoleName)}
      </ChitStatus>
      <span className="text-white/20">•</span>
      <span>Joined {formatDate(joinedDate)}</span>
    </span>
  );

  return (
    <Chit interactive={false}>
      <ChitContent>
        <ChitMain>
          <ChitAvatar fallback={displayName?.substring(0, 2) || 'U'} size="md" />
          <ChitText
            title={displayName}
            subtitle={subtitle}
            description={description}
            titleClassName="text-base"
            subtitleClassName="text-white/40"
            descriptionClassName="text-white/30 text-xs"
          />
        </ChitMain>
        <Popover>
          {/* The PopoverTrigger render-prop returns the trigger element itself.
              Previously this nested ChitMoreActions (which renders its own
              <button>) inside an outer <button>, producing a hydration error.
              We render the icon directly inside a single trigger button. */}
          <PopoverTrigger render={() => (
            <button
              type="button"
              aria-label="Member actions"
              className="text-white/40 bg-transparent border-none cursor-pointer p-2 rounded-md flex items-center hover:bg-white/10 transition-colors"
            >
              <MoreVertIcon className="w-5 h-5" />
            </button>
          )} />
          <PopoverContent align="end" className="w-48 p-1">
            <div className="flex flex-col gap-1">
              <button
                onClick={() => onAction(userId, 'edit')}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/5 rounded-lg transition-colors text-left"
              >
                <EditIcon className="w-4 h-4" />
                Edit Member
              </button>
              <button
                onClick={() => onAction(userId, 'remove')}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-left"
              >
                <DeleteIcon className="w-4 h-4" />
                Remove Member
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </ChitContent>
    </Chit>
  );
};
